// Reports & Analytics — read-only analytics dashboard.
//
// Spec: TKOS_MASTER_CONTEXT.md §Reports & Analytics + inventory_sections/01_retailer.md
// §Reports & Analytics. Source-of-truth render lives in portal-standalone.html
// 21717-21782 (`renderReports` + `DATA`). Source-of-truth uses canvas charts;
// per the rebuild contract we redraw with plain SVG (no chart library).
//
// Backend wiring
// --------------
// kitchenos-api currently exposes NO `/api/portal/reports/*` endpoints
// (verified 2026-05 against /var/www/kitchenos-api/server.js — all
// `/api/portal/*` routes are messaging/email-related). When the analytics
// endpoints land we fan out to:
//
//   GET /api/portal/reports/summary?period=…     KPIs + AOV
//   GET /api/portal/reports/revenue?period=…     monthly series this/last year
//   GET /api/portal/reports/funnel?period=…      conversion stages
//   GET /api/portal/reports/by-day?period=…      busiest days of week
//   GET /api/portal/reports/by-supplier?period=… YTD supplier mix
//
// Until then we resolve via Promise.allSettled and gracefully fall back to
// realistic kitchen-retail demo numbers (mirroring the legacy DATA block) so
// the charts still render meaningfully. A "demo data" badge informs the user.
//
// Period selector
// ---------------
// 30 days / 90 days / 6 months / YTD. Switching periods aborts any in-flight
// fetch and re-loads. Initial period is 6 months (matches source-of-truth
// default — its preset reflects the most useful quarterly+horizon view).

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import { demoDataAllowed } from "../../shell/onboarding";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Period domain ────────────────────────────────────────────────────────────

type PeriodKey = "30d" | "90d" | "6m" | "ytd";

interface PeriodSpec {
  key: PeriodKey;
  label: string;
  /** Shown inside the KPI labels e.g. "Revenue (6m)". */
  short: string;
  /** Number of monthly buckets the trend chart should render. */
  months: number;
}

const PERIODS: readonly PeriodSpec[] = [
  { key: "30d", label: "30 days", short: "30d", months: 1 },
  { key: "90d", label: "90 days", short: "90d", months: 3 },
  { key: "6m",  label: "6 months", short: "6m", months: 6 },
  { key: "ytd", label: "YTD",      short: "YTD", months: 12 },
];

const DEFAULT_PERIOD: PeriodKey = "6m";

// ── Types (server response shapes — also used as view-model) ─────────────────

interface KpiDelta {
  /** Numeric value of the delta. May be percent (0.07 = +7%) or pts (0.04 = +4 pts). */
  value: number;
  /** Direction is derived from sign; we store explicitly for clarity. */
  direction: "up" | "down" | "flat";
  /** Free-form caption shown after the figure, e.g. "vs last year". */
  caption: string;
  /** Whether `value` should be rendered as percent (0.071 → 7.1%) or pts (0.042 → 4.2 pts). */
  format: "percent" | "pts" | "raw";
}

interface KpiBlock {
  revenue: { totalGbp: number; delta: KpiDelta };
  conversion: { ratePct: number; delta: KpiDelta };
  aov: { gbp: number; delta: KpiDelta };
  busiestDay: { day: string; jobs: number };
}

interface RevenueSeries {
  months: string[];
  thisYearGbp: number[];
  lastYearGbp: number[];
}

interface FunnelStage {
  stage: string;
  count: number;
  /** Optional accent — if absent we colour by index. */
  colour?: string;
}

interface DayBucket {
  /** "Mon" .. "Sun". */
  d: string;
  /** Job count. */
  v: number;
}

interface SupplierSlice {
  name: string;
  gbp: number;
  colour?: string;
}

/** One "How did you hear about us?" referral source — lead volume + conversion. */
interface ReferralSlice {
  source: string;
  leads: number;
  won: number;
  conversionPct: number;
}

/** One competitor and how often it's come up in customer messages. */
interface CompetitorSlice {
  competitor: string;
  label: string;
  mentions: number;
}

interface ReportsView {
  kpis: KpiBlock;
  revenue: RevenueSeries;
  funnel: FunnelStage[];
  busyDays: DayBucket[];
  suppliers: SupplierSlice[];
  sources: ReferralSlice[];
  competitors: CompetitorSlice[];
  /** True when ANY of the view fell back to demo. */
  isDemo: boolean;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const PCT = new Intl.NumberFormat("en-GB", {
  style: "percent",
  maximumFractionDigits: 1,
});

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDelta(d: KpiDelta): string {
  const arrow = d.direction === "up" ? "▲" : d.direction === "down" ? "▼" : "▬";
  const abs = Math.abs(d.value);
  let figure: string;
  if (d.format === "percent") {
    figure = PCT.format(abs);
  } else if (d.format === "pts") {
    figure = `${abs.toFixed(1)} pts`;
  } else {
    figure = abs.toFixed(1);
  }
  return `${arrow} ${figure} ${d.caption}`;
}

// ── Empty fallback ───────────────────────────────────────────────────────────

/**
 * Empty/zero state used until the retailer-facing reports endpoints land.
 * Chart scaffolding (month axis, funnel stage labels, weekday buckets) is kept
 * so the components render intact, but every figure is zero — no demo data.
 */
function demoView(period: PeriodSpec): ReportsView {
  const monthsLabels = monthLabelsForPeriod(period.months);
  const zeros = monthsLabels.map(() => 0);

  const funnel: FunnelStage[] = [
    { stage: "Leads",    count: 0, colour: "#6ca0ff" },
    { stage: "Design",   count: 0, colour: "#7ea9ff" },
    { stage: "Quoted",   count: 0, colour: "#ff9f1c" },
    { stage: "Approved", count: 0, colour: "#ffb34a" },
    { stage: "Won",      count: 0, colour: "#6de0b3" },
  ];

  const busyDays: DayBucket[] = [
    { d: "Mon", v: 0 }, { d: "Tue", v: 0 }, { d: "Wed", v: 0 },
    { d: "Thu", v: 0 }, { d: "Fri", v: 0 }, { d: "Sat", v: 0 }, { d: "Sun", v: 0 },
  ];

  return {
    kpis: {
      revenue: {
        totalGbp: 0,
        delta: { value: 0, direction: "flat", caption: "vs last year", format: "percent" },
      },
      conversion: {
        ratePct: 0,
        delta: { value: 0, direction: "flat", caption: "vs last period", format: "pts" },
      },
      aov: {
        gbp: 0,
        delta: { value: 0, direction: "flat", caption: "vs last period", format: "percent" },
      },
      busiestDay: { day: "—", jobs: 0 },
    },
    revenue: {
      months: monthsLabels,
      thisYearGbp: zeros,
      lastYearGbp: [...zeros],
    },
    funnel,
    busyDays,
    suppliers: [],
    sources: [],
    competitors: [],
    isDemo: true,
  };
}

/** Last N month abbreviations (oldest → newest) ending with the current month. */
function monthLabelsForPeriod(n: number): string[] {
  const fmt = new Intl.DateTimeFormat("en-GB", { month: "short" });
  const out: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(fmt.format(d));
  }
  return out;
}

// ── Backend fetch ────────────────────────────────────────────────────────────

interface SummaryResponse {
  kpis: KpiBlock;
}

async function loadView(
  ctx: PageContext,
  period: PeriodSpec,
  signal: AbortSignal,
): Promise<ReportsView> {
  // Fan out — none of these exist yet, so 404/501 → demo. Network errors
  // bubble up to the caller (which will demote to demo + toast).
  const q = `?period=${encodeURIComponent(period.key)}`;
  const [summaryRes, revenueRes, funnelRes, dayRes, suppRes, srcRes, compRes] = await Promise.allSettled([
    ctx.api<SummaryResponse>(`/portal/reports/summary${q}`,    { signal }),
    ctx.api<RevenueSeries>(`/portal/reports/revenue${q}`,      { signal }),
    ctx.api<FunnelStage[]>(`/portal/reports/funnel${q}`,       { signal }),
    ctx.api<DayBucket[]>(`/portal/reports/by-day${q}`,         { signal }),
    ctx.api<SupplierSlice[]>(`/portal/reports/by-supplier${q}`, { signal }),
    ctx.api<ReferralSlice[]>(`/portal/reports/by-source${q}`,  { signal }),
    ctx.api<CompetitorSlice[]>(`/portal/reports/by-competitor${q}`, { signal }),
  ]);

  if (signal.aborted) {
    throw new DOMException("aborted", "AbortError");
  }

  const isMissing = (r: PromiseSettledResult<unknown>): boolean =>
    r.status === "rejected" &&
    r.reason instanceof ApiError &&
    (r.reason.status === 404 || r.reason.status === 501);

  // If everything is missing → fall back wholesale to demo (no toast — expected).
  const allMissing = [summaryRes, revenueRes, funnelRes, dayRes, suppRes, srcRes, compRes].every(isMissing);
  if (allMissing) {
    return demoView(period);
  }

  // If anything is rejected with a NON-404, surface the first such error so the
  // page can toast and demote to demo.
  for (const r of [summaryRes, revenueRes, funnelRes, dayRes, suppRes, srcRes, compRes]) {
    if (r.status === "rejected" && !isMissing(r)) {
      throw r.reason;
    }
  }

  // Hybrid: backfill any individually-missing slice from the demo view.
  const fallback = demoView(period);
  const view: ReportsView = {
    kpis: summaryRes.status === "fulfilled" ? summaryRes.value.kpis : fallback.kpis,
    revenue: revenueRes.status === "fulfilled" ? revenueRes.value : fallback.revenue,
    funnel: funnelRes.status === "fulfilled" ? funnelRes.value : fallback.funnel,
    busyDays: dayRes.status === "fulfilled" ? dayRes.value : fallback.busyDays,
    suppliers: suppRes.status === "fulfilled" ? suppRes.value : fallback.suppliers,
    sources: srcRes.status === "fulfilled" ? srcRes.value : fallback.sources,
    competitors: compRes.status === "fulfilled" ? compRes.value : fallback.competitors,
    isDemo:
      summaryRes.status !== "fulfilled" ||
      revenueRes.status !== "fulfilled" ||
      funnelRes.status !== "fulfilled" ||
      dayRes.status !== "fulfilled" ||
      suppRes.status !== "fulfilled" ||
      srcRes.status !== "fulfilled" ||
      compRes.status !== "fulfilled",
  };
  return view;
}

// ── SVG chart builders ───────────────────────────────────────────────────────

const CHART_PRIMARY = "#F59E0B";
const CHART_GRID = "rgba(148, 163, 184, 0.18)";
const CHART_GRID_TXT = "#9CA3AF";

/** Trend chart: this year (solid amber) vs last year (dashed grey). */
function svgTrend(series: RevenueSeries): string {
  const W = 720;
  const H = 260;
  const padL = 56;
  const padR = 16;
  const padT = 14;
  const padB = 32;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const all = [...series.thisYearGbp, ...series.lastYearGbp, 0];
  const max = Math.max(...all) * 1.1 || 1;

  const xFor = (i: number, n: number): number => padL + plotW * (n <= 1 ? 0 : i / (n - 1));
  const yFor = (v: number): number => padT + plotH * (1 - v / max);

  // Gridlines + Y labels (5 horizontal divisions).
  const gridParts: string[] = [];
  for (let g = 0; g <= 4; g++) {
    const y = padT + plotH * (g / 4);
    const v = max - (max * g) / 4;
    gridParts.push(
      `<line x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}" stroke="${CHART_GRID}" stroke-width="1"/>`,
      `<text x="${padL - 6}" y="${y + 3}" fill="${CHART_GRID_TXT}" font-size="10" text-anchor="end">£${Math.round(v / 1000)}k</text>`,
    );
  }

  // X labels (months).
  const xLabels = series.months
    .map((m, i) => {
      const x = xFor(i, series.months.length);
      return `<text x="${x}" y="${H - 10}" fill="${CHART_GRID_TXT}" font-size="10" text-anchor="middle">${escapeHtml(m)}</text>`;
    })
    .join("");

  // Build path strings.
  const pathFor = (vals: number[]): string =>
    vals
      .map((v, i) => `${i === 0 ? "M" : "L"} ${xFor(i, vals.length).toFixed(1)} ${yFor(v).toFixed(1)}`)
      .join(" ");

  const thisPath = pathFor(series.thisYearGbp);
  const lastPath = pathFor(series.lastYearGbp);

  // Area fill under "this year" line.
  const areaPath =
    thisPath +
    ` L ${xFor(series.thisYearGbp.length - 1, series.thisYearGbp.length).toFixed(1)} ${(padT + plotH).toFixed(1)}` +
    ` L ${xFor(0, series.thisYearGbp.length).toFixed(1)} ${(padT + plotH).toFixed(1)} Z`;

  // Dots on "this year".
  const dots = series.thisYearGbp
    .map((v, i) => {
      const x = xFor(i, series.thisYearGbp.length);
      const y = yFor(v);
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.2" fill="${CHART_PRIMARY}"/>` +
        `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.3" fill="rgba(255,255,255,0.95)"/>`;
    })
    .join("");

  return `
    <svg class="rep-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Monthly revenue trend">
      ${gridParts.join("")}
      ${xLabels}
      <defs>
        <linearGradient id="rep-trend-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="${CHART_PRIMARY}" stop-opacity="0.32"/>
          <stop offset="100%" stop-color="${CHART_PRIMARY}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${areaPath}" fill="url(#rep-trend-fill)"/>
      <path d="${lastPath}" stroke="${CHART_GRID_TXT}" stroke-width="1.6" stroke-dasharray="4 4" fill="none" opacity="0.85"/>
      <path d="${thisPath}" stroke="${CHART_PRIMARY}" stroke-width="2.2" fill="none"/>
      ${dots}
    </svg>
  `;
}

/** Funnel: 5 horizontal bars decreasing in width (centered), with count labels. */
function svgFunnel(stages: FunnelStage[]): string {
  const W = 320;
  const H = 260;
  const padX = 16;
  const padY = 8;
  const rowH = (H - padY * 2) / Math.max(stages.length, 1);
  const max = stages[0]?.count ?? 1;

  const fallbackColours = ["#6ca0ff", "#7ea9ff", "#ff9f1c", "#ffb34a", "#6de0b3"];

  const rows = stages
    .map((st, i) => {
      const colour = st.colour ?? fallbackColours[i % fallbackColours.length]!;
      const frac = max > 0 ? st.count / max : 0;
      const barW = (W - padX * 2) * frac;
      const cx = W / 2;
      const top = padY + i * rowH + 4;
      const bot = padY + (i + 1) * rowH - 6;
      const left = cx - barW / 2;
      const right = cx + barW / 2;
      // Slight inset on bottom edge to give a trapezoid shape.
      const inset = Math.min(4, barW / 4);
      const points = [
        [left, top],
        [right, top],
        [right - inset, bot],
        [left + inset, bot],
      ]
        .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
        .join(" ");
      const labelY = top + (bot - top) / 2 + 4;
      const label = `${st.stage} · ${st.count}`;
      return `
        <polygon points="${points}" fill="${colour}" opacity="0.9"/>
        <text x="${cx}" y="${labelY.toFixed(1)}" fill="#ffffff" font-size="12" font-weight="600" text-anchor="middle">${escapeHtml(label)}</text>
      `;
    })
    .join("");

  return `
    <svg class="rep-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Quote conversion funnel">
      ${rows}
    </svg>
  `;
}

/** Busiest days: vertical bars Mon-Sun with count labels above. */
function svgBusyDays(days: DayBucket[]): string {
  const W = 480;
  const H = 260;
  const padL = 22;
  const padR = 14;
  const padT = 22;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...days.map((d) => d.v)) * 1.1;
  const gap = 12;
  const barW = (plotW - gap * (days.length - 1)) / days.length;

  const bars = days
    .map((d, i) => {
      const x = padL + i * (barW + gap);
      const h = plotH * (d.v / max);
      const y = padT + plotH - h;
      const r = Math.min(5, barW / 4);
      // Rounded-top rect via path.
      const path = `M ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + barW - r} ${y} Q ${x + barW} ${y} ${x + barW} ${y + r} L ${x + barW} ${y + h} L ${x} ${y + h} Z`;
      return `
        <path d="${path}" fill="${CHART_PRIMARY}"/>
        <text x="${(x + barW / 2).toFixed(1)}" y="${(y - 6).toFixed(1)}" fill="#f0f2f6" font-size="11" font-weight="600" text-anchor="middle">${d.v}</text>
        <text x="${(x + barW / 2).toFixed(1)}" y="${H - 10}" fill="${CHART_GRID_TXT}" font-size="11" text-anchor="middle">${escapeHtml(d.d)}</text>
      `;
    })
    .join("");

  // Baseline.
  const baseline = `<line x1="${padL}" y1="${padT + plotH}" x2="${W - padR}" y2="${padT + plotH}" stroke="${CHART_GRID}" stroke-width="1"/>`;

  return `
    <svg class="rep-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Busiest days of the week">
      ${baseline}
      ${bars}
    </svg>
  `;
}

/** Revenue by supplier: vertical bar list with £ labels and percentage. */
/** Ranked referral-source list: bar = lead volume, with conversion % alongside.
 *  The highest-volume source isn't always the best-converting — showing both
 *  side by side is the insight. */
function renderSourceBars(sources: ReferralSlice[]): string {
  if (sources.length === 0) {
    return `<div class="rep-empty">No referral sources recorded yet — they'll appear as leads come in.</div>`;
  }
  const max = Math.max(...sources.map((s) => s.leads), 1);
  const colours = ["#ff9f1c", "#6ca0ff", "#6de0b3", "#c5a8ff", "#ff8ea0", "#ffd166", "#9bd1ff"];
  return `
    <ul class="rep-list" role="list">
      ${sources
        .map((s, i) => {
          const widthPct = (s.leads / max) * 100;
          const colour = colours[i % colours.length]!;
          return `
            <li class="rep-row">
              <div class="rep-row__label">
                <span class="rep-row__swatch" aria-hidden="true" style="--rep-swatch:${colour}"></span>
                <span>${escapeHtml(s.source)}</span>
              </div>
              <div class="rep-row__bar" aria-hidden="true">
                <div class="rep-row__bar-fill" style="width:${widthPct.toFixed(2)}%; background:${colour}"></div>
              </div>
              <div class="rep-row__val">
                <span>${s.leads} lead${s.leads === 1 ? "" : "s"}</span>
                <span class="rep-row__pct">${PCT.format((s.conversionPct || 0) / 100)} won</span>
              </div>
            </li>
          `;
        })
        .join("")}
    </ul>
  `;
}

/** Ranked competitor-mention list: bar = how often each competitor comes up in
 *  customer conversations. Market intelligence — who you're really up against. */
function renderCompetitorBars(competitors: CompetitorSlice[]): string {
  if (competitors.length === 0) {
    return `<div class="rep-empty">No competitor mentions yet — Kit logs them as they come up in customer messages.</div>`;
  }
  const max = Math.max(...competitors.map((c) => c.mentions), 1);
  const colours = ["#ef4444", "#f97316", "#ff9f1c", "#c5a8ff", "#6ca0ff", "#6de0b3", "#9bd1ff"];
  return `
    <ul class="rep-list" role="list">
      ${competitors
        .map((c, i) => {
          const widthPct = (c.mentions / max) * 100;
          const colour = colours[i % colours.length]!;
          return `
            <li class="rep-row">
              <div class="rep-row__label">
                <span class="rep-row__swatch" aria-hidden="true" style="--rep-swatch:${colour}"></span>
                <span>${escapeHtml(c.label)}</span>
              </div>
              <div class="rep-row__bar" aria-hidden="true">
                <div class="rep-row__bar-fill" style="width:${widthPct.toFixed(2)}%; background:${colour}"></div>
              </div>
              <div class="rep-row__val">
                <span>${c.mentions} mention${c.mentions === 1 ? "" : "s"}</span>
              </div>
            </li>
          `;
        })
        .join("")}
    </ul>
  `;
}

function renderSupplierBars(suppliers: SupplierSlice[]): string {
  if (suppliers.length === 0) {
    return `<div class="rep-empty">No supplier revenue recorded.</div>`;
  }
  const total = suppliers.reduce((a, b) => a + b.gbp, 0);
  const max = Math.max(...suppliers.map((s) => s.gbp), 1);
  const fallbackColours = ["#ff9f1c", "#6ca0ff", "#6de0b3", "#c5a8ff", "#ff8ea0", "#ffd166", "#9bd1ff"];

  return `
    <ul class="rep-list" role="list">
      ${suppliers
        .map((s, i) => {
          const pct = total > 0 ? s.gbp / total : 0;
          const widthPct = (s.gbp / max) * 100;
          const colour = s.colour ?? fallbackColours[i % fallbackColours.length]!;
          return `
            <li class="rep-row">
              <div class="rep-row__label">
                <span class="rep-row__swatch" aria-hidden="true" style="--rep-swatch:${colour}"></span>
                <span>${escapeHtml(s.name)}</span>
              </div>
              <div class="rep-row__bar" aria-hidden="true">
                <div class="rep-row__bar-fill" style="width:${widthPct.toFixed(2)}%; background:${colour}"></div>
              </div>
              <div class="rep-row__val">
                <span>${escapeHtml(GBP.format(s.gbp))}</span>
                <span class="rep-row__pct">${PCT.format(pct)}</span>
              </div>
            </li>
          `;
        })
        .join("")}
    </ul>
  `;
}

// ── Rendering ────────────────────────────────────────────────────────────────

interface ShellRefs {
  rangeEl: HTMLElement;
  badgeEl: HTMLElement;
  bannerEl: HTMLElement;
  kpisEl: HTMLElement;
  trendEl: HTMLElement;
  funnelEl: HTMLElement;
  daysEl: HTMLElement;
  suppliersEl: HTMLElement;
  sourcesEl: HTMLElement;
  competitorsEl: HTMLElement;
  trendSubEl: HTMLElement;
  funnelSubEl: HTMLElement;
}

function renderShell(container: HTMLElement, period: PeriodSpec): ShellRefs {
  container.innerHTML = `
    <section class="rep-root" aria-label="Reports and analytics">
      <header class="rep-hero">
        <div class="rep-hero__titles">
          <h1 class="rep-title">Reports &amp; Analytics</h1>
          <p class="rep-subtitle">Business performance across sales, quotes, deliveries and suppliers</p>
        </div>
        <div class="rep-hero__right">
          <span class="rep-badge" data-role="demo-badge" hidden>Demo data · live analytics connect later</span>
          <div class="rep-range" role="tablist" aria-label="Time period" data-role="range">
            ${PERIODS.map(
              (p) => `
                <button type="button" role="tab" data-period="${p.key}"
                  class="rep-range__btn${p.key === period.key ? " is-active" : ""}"
                  aria-selected="${p.key === period.key}">${escapeHtml(p.label)}</button>
              `,
            ).join("")}
          </div>
        </div>
      </header>

      <div class="rep-banner" data-role="banner" hidden></div>

      <div class="rep-kpis" data-role="kpis" role="list"></div>

      <div class="rep-grid">
        <section class="rep-card rep-card--col8" aria-labelledby="rep-trend-title">
          <header class="rep-card__head">
            <h3 id="rep-trend-title">Monthly revenue trend</h3>
            <p class="rep-card__sub" data-role="trend-sub">This period vs same period last year</p>
          </header>
          <div class="rep-card__body" data-role="trend"></div>
          <div class="rep-legend">
            <span><i style="background:${CHART_PRIMARY}"></i>This year</span>
            <span><i class="rep-legend__dashed"></i>Last year</span>
          </div>
        </section>

        <section class="rep-card rep-card--col4" aria-labelledby="rep-funnel-title">
          <header class="rep-card__head">
            <h3 id="rep-funnel-title">Quote conversion funnel</h3>
            <p class="rep-card__sub" data-role="funnel-sub">Lead to Won</p>
          </header>
          <div class="rep-card__body" data-role="funnel"></div>
        </section>

        <section class="rep-card rep-card--col6" aria-labelledby="rep-days-title">
          <header class="rep-card__head">
            <h3 id="rep-days-title">Busiest days of the week</h3>
            <p class="rep-card__sub">Average job events per weekday</p>
          </header>
          <div class="rep-card__body" data-role="days"></div>
        </section>

        <section class="rep-card rep-card--col6" aria-labelledby="rep-supp-title">
          <header class="rep-card__head">
            <h3 id="rep-supp-title">Revenue by supplier</h3>
            <p class="rep-card__sub">Share of total supplier spend</p>
          </header>
          <div class="rep-card__body" data-role="suppliers"></div>
        </section>

        <section class="rep-card rep-card--col6" aria-labelledby="rep-source-title">
          <header class="rep-card__head">
            <h3 id="rep-source-title">Referral sources</h3>
            <p class="rep-card__sub">How customers heard about you — lead volume and conversion</p>
          </header>
          <div class="rep-card__body" data-role="sources"></div>
        </section>

        <section class="rep-card rep-card--col6" aria-labelledby="rep-comp-title">
          <header class="rep-card__head">
            <h3 id="rep-comp-title">Competitors mentioned</h3>
            <p class="rep-card__sub">Who customers are comparing you with</p>
          </header>
          <div class="rep-card__body" data-role="competitors"></div>
        </section>
      </div>
    </section>
  `;

  return {
    rangeEl: container.querySelector<HTMLElement>("[data-role='range']")!,
    badgeEl: container.querySelector<HTMLElement>("[data-role='demo-badge']")!,
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    kpisEl: container.querySelector<HTMLElement>("[data-role='kpis']")!,
    trendEl: container.querySelector<HTMLElement>("[data-role='trend']")!,
    funnelEl: container.querySelector<HTMLElement>("[data-role='funnel']")!,
    daysEl: container.querySelector<HTMLElement>("[data-role='days']")!,
    suppliersEl: container.querySelector<HTMLElement>("[data-role='suppliers']")!,
    sourcesEl: container.querySelector<HTMLElement>("[data-role='sources']")!,
    competitorsEl: container.querySelector<HTMLElement>("[data-role='competitors']")!,
    trendSubEl: container.querySelector<HTMLElement>("[data-role='trend-sub']")!,
    funnelSubEl: container.querySelector<HTMLElement>("[data-role='funnel-sub']")!,
  };
}

interface KpiCardSpec {
  label: string;
  value: string;
  delta: KpiDelta;
}

function renderKpis(host: HTMLElement, view: ReportsView, period: PeriodSpec): void {
  const cards: KpiCardSpec[] = [
    {
      label: `Revenue (${period.short})`,
      value: GBP.format(view.kpis.revenue.totalGbp),
      delta: view.kpis.revenue.delta,
    },
    {
      label: "Quote conversion",
      value: PCT.format(view.kpis.conversion.ratePct),
      delta: view.kpis.conversion.delta,
    },
    {
      label: "Average order value",
      value: GBP.format(view.kpis.aov.gbp),
      delta: view.kpis.aov.delta,
    },
    {
      label: "Busiest day",
      value: view.kpis.busiestDay.day,
      delta: {
        value: view.kpis.busiestDay.jobs,
        direction: "up",
        caption: "jobs · peak",
        format: "raw",
      },
    },
  ];
  host.innerHTML = cards
    .map(
      (c) => `
        <article class="rep-kpi" role="listitem">
          <div class="rep-kpi__lbl">${escapeHtml(c.label)}</div>
          <div class="rep-kpi__val">${escapeHtml(c.value)}</div>
          <div class="rep-kpi__delta rep-kpi__delta--${c.delta.direction}">${escapeHtml(formatDelta(c.delta))}</div>
        </article>
      `,
    )
    .join("");
}

function renderAll(refs: ShellRefs, view: ReportsView, period: PeriodSpec): void {
  renderKpis(refs.kpisEl, view, period);
  refs.trendEl.innerHTML = svgTrend(view.revenue);
  refs.funnelEl.innerHTML = svgFunnel(view.funnel);
  refs.daysEl.innerHTML = svgBusyDays(view.busyDays);
  refs.suppliersEl.innerHTML = renderSupplierBars(view.suppliers);
  refs.sourcesEl.innerHTML = renderSourceBars(view.sources);
  refs.competitorsEl.innerHTML = renderCompetitorBars(view.competitors);
  refs.trendSubEl.textContent = `Last ${period.label.toLowerCase()} vs same period last year`;
  refs.funnelSubEl.textContent = `Lead to Won, last ${period.label.toLowerCase()}`;
  refs.badgeEl.hidden = !view.isDemo;
}

// ── Page ─────────────────────────────────────────────────────────────────────

const reportsPage: Page = {
  name: "reports",
  label: "Reports & Analytics",
  icon: "📊",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let currentPeriod: PeriodSpec = PERIODS.find((p) => p.key === DEFAULT_PERIOD)!;
    const refs = renderShell(container, currentPeriod);

    let controller: AbortController | null = null;
    let disposed = false;

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        refs.bannerEl.hidden = true;
        refs.bannerEl.textContent = "";
        refs.bannerEl.classList.remove("rep-banner--error");
        return;
      }
      refs.bannerEl.hidden = false;
      refs.bannerEl.textContent = msg;
      refs.bannerEl.classList.toggle("rep-banner--error", kind === "error");
    };

    const setActiveTab = (key: PeriodKey): void => {
      const buttons = refs.rangeEl.querySelectorAll<HTMLButtonElement>("[data-period]");
      buttons.forEach((btn) => {
        const active = btn.dataset.period === key;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-selected", String(active));
      });
    };

    const load = async (period: PeriodSpec): Promise<void> => {
      if (disposed) return;
      // Abort any in-flight previous request.
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;

      container.querySelector(".rep-grid")?.classList.add("is-loading");

      try {
        const view = await loadView(ctx, period, signal);
        if (disposed || signal.aborted) return;
        // Onboarding gate: a real (not-yet-onboarded) retailer account must
        // never be framed with demo data. The figures are already zeroed; drop
        // the `isDemo` flag so the "Demo data" badge + toast don't appear.
        if (!demoDataAllowed(ctx)) view.isDemo = false;
        renderAll(refs, view, period);
        if (view.isDemo) {
          setBanner(
            "Live analytics not yet available — showing realistic demo figures. Backend endpoints pending.",
            "info",
          );
        } else {
          setBanner(null);
        }
      } catch (err) {
        if (signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load reports (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load reports";
        // Demote to the empty/zero view so the page still renders. For a real
        // (not-yet-onboarded) account, suppress the demo framing.
        const fallback = demoView(period);
        if (!demoDataAllowed(ctx)) fallback.isDemo = false;
        renderAll(refs, fallback, period);
        setBanner(`${msg}.`, "error");
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) {
          container.querySelector(".rep-grid")?.classList.remove("is-loading");
        }
      }
    };

    // Range button clicks → switch period + reload (which aborts in-flight).
    const onRangeClick = (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>("[data-period]");
      if (!btn) return;
      const key = btn.dataset.period as PeriodKey | undefined;
      if (!key) return;
      const next = PERIODS.find((p) => p.key === key);
      if (!next || next.key === currentPeriod.key) return;
      currentPeriod = next;
      setActiveTab(key);
      void load(next);
    };
    refs.rangeEl.addEventListener("click", onRangeClick);

    // Initial load.
    void load(currentPeriod);

    return (): void => {
      disposed = true;
      refs.rangeEl.removeEventListener("click", onRangeClick);
      controller?.abort();
      controller = null;
    };
  },
};

registerPage(reportsPage);

export default reportsPage;
